// share-modal — real feature (not promo). Shows current URL + QR + copy button.
//
// Mount target: #share-share-modal-host. Subscribes to state.shareDialog.open.
// QR generated via qrcode-generator npm package (~4 KB gzipped). Copy uses
// navigator.clipboard.writeText with execCommand fallback for non-secure
// contexts.

import qrcode from 'qrcode-generator';
import { subscribe, getState, setState } from '../state.js';

export interface ShareModalHandle {
  unmount: () => void;
}

function buildModal(): HTMLElement {
  const backdrop = document.createElement('div');
  backdrop.className = 'share-modal-backdrop';
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-labelledby', 'share-modal-title');

  const modal = document.createElement('div');
  modal.className = 'share-modal';

  const title = document.createElement('h2');
  title.id = 'share-modal-title';
  title.className = 'share-modal-title';
  title.textContent = 'Share';
  modal.appendChild(title);

  const urlRow = document.createElement('div');
  urlRow.className = 'share-modal-url-row';

  const urlInput = document.createElement('input');
  urlInput.className = 'share-modal-url';
  urlInput.type = 'text';
  urlInput.readOnly = true;
  urlInput.value = location.href;

  const copyBtn = document.createElement('button');
  copyBtn.className = 'share-modal-copy';
  copyBtn.type = 'button';
  copyBtn.textContent = 'Copy';

  urlRow.appendChild(urlInput);
  urlRow.appendChild(copyBtn);
  modal.appendChild(urlRow);

  const qrWrap = document.createElement('div');
  qrWrap.className = 'share-modal-qr';
  // qrcode-generator: createQRCode(typeNumber=0 auto, errorCorrectionLevel='M')
  const qr = qrcode(0, 'M');
  qr.addData(location.href);
  qr.make();
  qrWrap.innerHTML = qr.createSvgTag({ scalable: true });
  modal.appendChild(qrWrap);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'share-modal-close';
  closeBtn.type = 'button';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '×';
  modal.appendChild(closeBtn);

  backdrop.appendChild(modal);
  return backdrop;
}

export function mountShareModal(host: Element): ShareModalHandle {
  function close(): void {
    setState({ shareDialog: { open: false } });
  }

  async function copy(label: HTMLButtonElement): Promise<void> {
    const url = location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback: select + execCommand
        const input = host.querySelector<HTMLInputElement>('.share-modal-url');
        if (input) {
          input.select();
          document.execCommand('copy');
        }
      }
      const originalText = label.textContent;
      label.textContent = 'Copied';
      setTimeout(() => {
        label.textContent = originalText;
      }, 2000);
    } catch {
      label.textContent = 'Error';
    }
  }

  function render(): void {
    const state = getState();
    if (!state.shareDialog.open) {
      host.textContent = '';
      return;
    }
    if (host.querySelector('.share-modal-backdrop')) return; // already rendered
    const modal = buildModal();
    modal.addEventListener('click', (e) => {
      if (e.target === modal) close();
    });
    modal.querySelector('.share-modal-close')!.addEventListener('click', close);
    modal.querySelector('.share-modal-copy')!.addEventListener('click', () => {
      copy(modal.querySelector<HTMLButtonElement>('.share-modal-copy')!);
    });
    host.appendChild(modal);
  }

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && getState().shareDialog.open) {
      close();
    }
  }

  render();
  const unsub = subscribe(render);
  document.addEventListener('keydown', onKeydown);

  return {
    unmount: () => {
      unsub();
      document.removeEventListener('keydown', onKeydown);
      host.textContent = '';
    },
  };
}
